import { useCallback, useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { useI18nContext } from "~/i18n/i18n-react";
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "../../ui/form";
import { Input } from "../../ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../ui/select";
import type { CreateRoomFormValues } from "./create-room-schema";
import { DateTimeField } from "./date-time-field";
import { LanguageSelectField } from "./language-select-field";

interface RoomDetailsStepProps {
	form: UseFormReturn<CreateRoomFormValues>;
}

const DURATION_OPTIONS = [15, 30, 45, 60, 75, 90, 105, 120] as const;

const formatDurationLabel = (mins: number): string => {
	if (mins >= 60) {
		const h = Math.floor(mins / 60);
		const m = mins % 60;
		return m ? `${h}h ${m}m` : `${h}h`;
	}
	return `${mins} m`;
};

/** Compute the initial duration from the default start/end values. */
const getInitialDuration = (startTime?: Date, endTime?: Date): number => {
	if (!(startTime && endTime)) {
		return 30;
	}
	const diffMins = Math.round(
		(endTime.getTime() - startTime.getTime()) / 60_000
	);
	const closest = DURATION_OPTIONS.reduce((prev, curr) =>
		Math.abs(curr - diffMins) < Math.abs(prev - diffMins) ? curr : prev
	);
	return closest;
};

/** Step one: what the room is called, its language, and when it runs. */
export const RoomDetailsStep = ({ form }: RoomDetailsStepProps) => {
	const { LL } = useI18nContext();
	const startTime = form.watch("startTime");
	const endTime = form.watch("endTime");
	const hasEndTime = Boolean(endTime);

	const [durationMins, setDurationMins] = useState<number>(() =>
		getInitialDuration(startTime, endTime)
	);

	/** Update endTime whenever startTime or duration changes. */
	const applyDuration = useCallback(
		(start: Date | undefined, mins: number) => {
			if (!start) {
				return;
			}
			const newEnd = new Date(start.getTime() + mins * 60_000);
			form.setValue("endTime", newEnd);
			form.trigger("endTime");
		},
		[form]
	);

	const handleDurationChange = (value: string) => {
		const mins = Number(value);
		setDurationMins(mins);
		applyDuration(startTime, mins);
	};

	/** When startTime changes externally (via the calendar), keep end in sync. */
	const handleStartTimeChange = useCallback(
		(value?: Date) => {
			if (value) {
				applyDuration(value, durationMins);
			}
		},
		[applyDuration, durationMins]
	);

	return (
		<div className="flex flex-col gap-4">
			<FormField
				control={form.control}
				name="name"
				render={({ field }) => (
					<FormItem>
						<FormLabel>
							{LL.CREATE_ROOM_FORM.NAME_LABEL()}
							{"*"}
						</FormLabel>
						<FormControl>
							<Input
								placeholder={LL.CREATE_ROOM_FORM.NAME_PLACEHOLDER()}
								{...field}
							/>
						</FormControl>
						<FormMessage />
					</FormItem>
				)}
			/>

			<LanguageSelectField control={form.control} />

			<div className="grid grid-cols-1 items-end gap-2 sm:grid-cols-[1fr_1fr_70px]">
				<DateTimeField
					control={form.control}
					fromDate={new Date()}
					hasEndTime={hasEndTime}
					label={LL.START_DATE_TIME()}
					name="startTime"
					onDateChange={handleStartTimeChange}
					trigger={form.trigger}
				/>

				<DateTimeField
					control={form.control}
					fromDate={
						startTime ? new Date(startTime.getTime() + 15 * 60_000) : new Date()
					}
					hasEndTime={hasEndTime}
					label={LL.END_DATE_TIME()}
					name="endTime"
					trigger={form.trigger}
				/>

				<div className="flex flex-col gap-2 text-sm">
					<FormLabel>Duration</FormLabel>
					<Select
						onValueChange={handleDurationChange}
						value={String(durationMins)}
					>
						<SelectTrigger className="px-2 sm:w-20">
							<SelectValue placeholder="Duration" />
						</SelectTrigger>
						<SelectContent>
							{DURATION_OPTIONS.map((mins) => (
								<SelectItem key={mins} value={String(mins)}>
									{formatDurationLabel(mins)}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			</div>
		</div>
	);
};
